// Coroner mailing-address helpers. Mirror of
// packages/nestjs/src/postgrid/coroner-address.ts (the cheque sender) — keep
// the two in sync; coronerAddress.test.ts runs the same cases.

// Structured recipient address for a coroner cheque, in PostGrid's field
// shape. addressLine2 holds a suite / unit / floor — PostGrid rejects a cheque
// when a suite is mashed into addressLine1 ("Missing Value: Suite identifier").
export interface CoronerAddress {
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  provinceOrState: string;
  postalOrZip: string;
}

export type UnitConfidence = 'high' | 'medium';

export interface UnitSplit {
  street: string;
  unit: string | null;
  confidence: UnitConfidence | null;
}

// Explicit unit designators, EN + FR. "Ste" only counts when followed by a
// unit token, so street names like "Ste-Catherine" don't match.
const DESIGNATOR =
  '(?:unit|suite|ste|apt|apartment|app|appt|appartement|bureau|room|rm|local|#)';
// A unit token: "4", "200", "12B", "B2", "PH1", or a lone letter ("Unit B").
const TOKEN = '(?:[A-Z]{0,2}\\d+[A-Z]?|[A-Z])';
const FLOOR = '(?:\\d+(?:st|nd|rd|th|e|er)\\s+(?:floor|fl|étage|etage)|(?:floor|étage|etage)\\s+\\d+)';

const LEADING_RE = new RegExp(
  `^(${DESIGNATOR}\\.?\\s*${TOKEN})\\s*(?:[,\\-–]\\s*)?(\\d.*)$`,
  'i',
);
const TRAILING_RE = new RegExp(
  `^(\\d.*?)\\s*[,\\s]\\s*(${DESIGNATOR}\\.?\\s*${TOKEN}|${FLOOR})\\.?$`,
  'i',
);
const LEADING_FLOOR_RE = new RegExp(`^(${FLOOR})\\s*,\\s*(\\d.*)$`, 'i');
// Canada Post convention: unit, hyphen, civic number ("503-18 Lower Village
// Gate"). Medium confidence — a hyphenated civic range ("12-14 Main St") looks
// the same, though those are rare on the OCC list.
const DASH_RE = /^([A-Z]?\d+[A-Z]?)\s*[-–]\s*(\d+[A-Z]?\s+[^\d\s].*)$/i;

/**
 * Pull a unit / apartment / suite out of a street line. Returns the street
 * without the unit, plus the unit text for addressLine2. PO boxes, rural
 * routes and bags are not units and stay on the street line.
 */
export function extractUnit(line: string | null | undefined): UnitSplit {
  const street = (line ?? '').trim().replace(/\s+/g, ' ');
  if (!street) return { street: '', unit: null, confidence: null };

  let m = LEADING_RE.exec(street);
  if (m && isUnitText(m[1])) {
    return { street: m[2].trim(), unit: tidyUnit(m[1]), confidence: 'high' };
  }
  m = LEADING_FLOOR_RE.exec(street);
  if (m) return { street: m[2].trim(), unit: tidyUnit(m[1]), confidence: 'high' };
  m = TRAILING_RE.exec(street);
  if (m && isUnitText(m[2])) {
    return {
      street: m[1].trim().replace(/[,\s]+$/, ''),
      unit: tidyUnit(m[2]),
      confidence: 'high',
    };
  }
  m = DASH_RE.exec(street);
  if (m) {
    return { street: m[2].trim(), unit: `Unit ${m[1].toUpperCase()}`, confidence: 'medium' };
  }
  return { street, unit: null, confidence: null };
}

// "#" needs a digit after it (so "#B" isn't a unit); named designators can take
// a lone letter ("Unit B").
function isUnitText(s: string): boolean {
  return !/^#/.test(s.trim()) || /\d/.test(s);
}

function tidyUnit(s: string): string {
  return s
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/^#\s+/, '#')
    .replace(/[,.]$/, '');
}

// The OCC dropdown gives us single-line addresses like
//   "56 Baby Point Cres, Toronto Ontario M6s2b9"
//   "18 Lower Village Gate, #503, Toronto ON M5P 3M1"
// PostGrid wants addressLine1 / addressLine2 / city / provinceOrState /
// postalOrZip split out. Any unit found on the street part goes to
// addressLine2.
export function parseCoronerAddress(
  addr: string | null | undefined,
): CoronerAddress | null {
  if (!addr) return null;
  const trimmed = addr.trim().replace(/\s+/g, ' ');
  // Pull the postal code off the end (Canadian format, optional space).
  const postalRe = /([A-Z]\d[A-Z])\s?(\d[A-Z]\d)\s*$/i;
  const m = postalRe.exec(trimmed);
  if (!m) return null;
  const postal = `${m[1].toUpperCase()} ${m[2].toUpperCase()}`;
  const beforePostal = trimmed
    .slice(0, m.index)
    .trim()
    .replace(/[,\s]+$/, '');

  // Strip trailing province ("Ontario", "ON", "Quebec", "QC").
  const provMatch = /^(.*?),?\s*(Ontario|ON|Quebec|QC|Québec)$/i.exec(beforePostal);
  if (!provMatch) return null;
  const province = /quebec|qc|québec/i.test(provMatch[2]) ? 'QC' : 'ON';
  const beforeProv = provMatch[1].trim().replace(/[,\s]+$/, '');

  // Last comma separates city from the street part.
  const lastComma = beforeProv.lastIndexOf(',');
  if (lastComma < 0) return null;
  const streetPart = beforeProv.slice(0, lastComma).trim();
  const city = beforeProv.slice(lastComma + 1).trim();
  if (!streetPart || !city) return null;

  const { street, unit } = extractUnit(streetPart);
  return {
    addressLine1: street,
    addressLine2: unit,
    city,
    provinceOrState: province,
    postalOrZip: postal,
  };
}

/**
 * Move a unit embedded in addressLine1 into addressLine2, when line 2 is
 * empty. Used on PostGrid's corrected address, which writes units Canada Post
 * style ("503-18 LOWER VILLAGE GATE"), so the admin's Unit field stays filled.
 */
export function splitUnitIntoLine2<T extends { addressLine1: string; addressLine2?: string | null }>(
  addr: T,
): T {
  if (addr.addressLine2?.trim()) return addr;
  const { street, unit } = extractUnit(addr.addressLine1);
  if (!unit) return addr;
  return { ...addr, addressLine1: street, addressLine2: unit };
}

// Inverse of parseCoronerAddress: collapse a structured address back to the
// single-line form the OCC dropdown / legacy `address` columns use, e.g.
//   "100 University Ave, 5th Floor, Toronto ON M5J 1V6"
export function formatCoronerAddress(addr: CoronerAddress): string {
  const line1 = [addr.addressLine1, addr.addressLine2]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(', ');
  const tail = [addr.city, addr.provinceOrState, addr.postalOrZip]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(' ');
  return [line1, tail].filter(Boolean).join(', ');
}

// The address block as it prints on the cheque envelope window: name, street,
// unit, then "City PROV  POSTAL".
export function formatMailingBlock(name: string, addr: CoronerAddress): string[] {
  return [
    name,
    addr.addressLine1,
    addr.addressLine2 ?? '',
    [addr.city, addr.provinceOrState].filter((s) => s?.trim()).join(' ') +
      (addr.postalOrZip?.trim() ? `  ${addr.postalOrZip.trim()}` : ''),
  ]
    .map((s) => s.trim())
    .filter(Boolean);
}

// Structured address from a `coroners` row: the structured columns when set,
// else parsed from the legacy single-line `address`.
export function coronerRowAddress(row: {
  address?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
}): CoronerAddress | null {
  if (row.address_line1) {
    return {
      addressLine1: row.address_line1,
      addressLine2: row.address_line2 ?? null,
      city: row.city ?? '',
      provinceOrState: row.province ?? '',
      postalOrZip: row.postal_code ?? '',
    };
  }
  return parseCoronerAddress(row.address);
}
